import React from "react";
import { FileTerminal } from "lucide-react";
import { Card } from "@/components/ui/card";

export const Terms: React.FC = () => {
  return (
    <div className='max-w-4xl mx-auto px-6 py-12'>
      {/* Page Header */}
      <div className='mb-10 text-center'>
        <div className='inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-4'>
          <FileTerminal className='h-8 w-8 text-blue-600' />
        </div>
        <h1 className='text-3xl font-bold text-gray-900 mb-2'>Terms & Conditions</h1>
        <p className='text-lg text-gray-600 max-w-2xl mx-auto'>
          Please read these terms carefully before using Stedshop LTD website and services.
        </p>
        <p className='text-sm text-gray-500 mt-2'>Last Updated: December 2025</p>
      </div>

      {/* Introduction */}
      <Card className='p-6 border-0 shadow-sm mb-8'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>Welcome to Stedshop LTD</h2>
        <p className='text-gray-700 mb-4'>
          The terms and conditions set out here govern our relationship with you and apply to your
          use of our website. By using our website you agree to be bound by the legally binding
          terms and conditions set out below. If you do not wish or agree to be legally bound by
          them then please do not access or use the Stedshop LTD website.
        </p>
        <p className='text-gray-700 mb-4'>
          We reserve the right to change these Terms & Conditions at any time and changes will be
          posted online on our website. Your continued use of the website after any such changes are
          posted shall signify your agreement to and acceptance of any such changes.
        </p>
        <p className='text-gray-700 mb-4'>
          This document is an electronic record in terms of the National Information Technology
          Development Agency (NITDA) Act, 2007 and the rules thereunder. This electronic record is
          generated by a computer system and does not require any physical or digital signatures.
        </p>
        <p className='text-gray-700'>
          By transacting on Stedshop.com.ng, you shall be contracting with Stedshop LTD, a company
          incorporated under the Companies and Allied Matters Act, 2020, with its registered office
          at No 3 Chinyelugo Udozor street awada obosi, Anambra, Nigeria.
        </p>
      </Card>

      {/* Registration Warranties */}
      <Card className='p-6 border-0 shadow-sm mb-8'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>Registration Warranties</h2>
        <p className='text-gray-700 mb-4'>
          When you register as a user of our website you warrant to us that:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700'>
          <li>
            All personal information provided by you is true, accurate and complete in all respects
            and that it is up to date
          </li>
          <li>
            You will immediately notify us of any changes to such personal information previously
            provided
          </li>
          <li>
            You will not misrepresent yourself as anyone else or utilize the name of any third party
            that you are not authorized to use. Similarly you will not use any credit or debit card
            or any other means of payment that does not belong to you or that you are not authorized
            to use
          </li>
        </ul>
      </Card>

      {/* 1. Membership Eligibility */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='eligibility'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>1. Membership Eligibility</h2>
        <p className='text-gray-700 mb-4'>
          Use of the Stedshop.com.ng website is available only to persons who can form legally
          binding contracts under Nigerian law. If you are under the age of 18, you may not register
          as a user or transact on Stedshop.com.ng.
        </p>
        <p className='text-gray-700'>
          Minors may only use the site through a parent or legal guardian who is a registered user.
          Stedshop LTD reserves the right to terminate any account found to belong to a minor.
        </p>
      </Card>

      {/* 2. Account and Registration */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='account'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>
          2. Your Account and Registration Obligations
        </h2>
        <p className='text-gray-700 mb-4'>
          You are responsible for maintaining the confidentiality of your User ID and Password and
          for all activities that occur under your account. You agree that any information you
          provide must be true, accurate, current, and complete.
        </p>
        <p className='text-gray-700'>
          Stedshop LTD reserves the right to suspend or terminate your account if it suspects any
          information provided is false, misleading, or violates these Terms.
        </p>
      </Card>

      {/* 3. Communications */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='communications'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>3. Communications</h2>
        <p className='text-gray-700 mb-4'>
          By using the Website or sending emails or data to Stedshop LTD, you agree to communicate
          via electronic records and consent to receive communications from us electronically (e.g.,
          via email or SMS).
        </p>
        <p className='text-gray-700'>
          We may also contact you via phone for order, shipment, or delivery updates. We will not
          use your contact details for promotional purposes without your consent.
        </p>
      </Card>

      {/* 4. Charges */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='charges'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>4. Charges</h2>
        <p className='text-gray-700 mb-4'>
          Membership on Stedshop.com.ng is free. Browsing and purchasing items incur no platform
          fee. However, Stedshop LTD reserves the right to introduce or modify fees for new or
          existing services at any time, with immediate effect upon posting on the website.
        </p>
        <p className='text-gray-700'>
          All prices are quoted in Nigerian Naira (₦) and are payable in that currency only.
        </p>
      </Card>

      {/* 5. Use of Website */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='use'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>5. Use of Stedshop.com.ng Website</h2>
        <p className='text-gray-700 mb-4'>
          You agree that your use of the Site shall comply with the following:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700'>
          <li>
            You will not upload, post, or transmit any content that is unlawful, harmful, harassing,
            defamatory, obscene, or infringes third-party rights (including intellectual property,
            privacy, or publicity rights)
          </li>
          <li>
            You will not engage in spamming, phishing, data mining, or unauthorized scraping of the
            Site
          </li>
          <li>
            You will not attempt to gain unauthorized access to the Site, user accounts, or backend
            systems
          </li>
          <li>
            You will not use the Site for any commercial purpose (e.g., resale, advertising,
            solicitation) without our prior written consent
          </li>
          <li>
            You will not impersonate any person or entity or misrepresent your affiliation with any
            person or entity
          </li>
        </ul>
        <p className='text-gray-700 mt-4'>
          Stedshop LTD reserves the right to monitor, edit, or remove any content that violates
          these Terms but assumes no obligation to do so. You remain solely responsible for all
          content you post.
        </p>
      </Card>

      {/* 6. Role as Reseller */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-blue-50' id='reseller'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>
          6. Role as a Reseller (Not Manufacturer)
        </h2>
        <p className='text-gray-700'>
          Stedshop LTD operates strictly as a reseller of finished clothing items. We do not
          manufacture, tailor, customize, or alter any garments. All products — whether sourced from
          global brands (e.g., Nike) or local vendors — are pre-made and sold as-is. Our business
          model is that of a standard online retail store, and these Terms reflect that of a
          reseller, not a bespoke tailor or manufacturer.
        </p>
      </Card>

      {/* 7. Returns & Refunds */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='returns'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>7. Returns & Refunds Policy</h2>

        <h3 className='text-lg font-semibold text-gray-900 mb-3 mt-6'>Eligibility</h3>
        <p className='text-gray-700 mb-3'>
          You may request a return within 7 (seven) working days from the date of delivery, provided
          that:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>The item is unworn, unused, and in its original condition</li>
          <li>
            All original tags, labels, and packaging (including the manufacturer's or vendor's cloth
            label) are intact and attached
          </li>
          <li>The item is returned in the same condition as received</li>
        </ul>

        <h3 className='text-lg font-semibold text-gray-900 mb-3 mt-6'>Non-Returnable Items</h3>
        <p className='text-gray-700 mb-3'>Returns will be rejected if:</p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>The return request is made after 7 working days</li>
          <li>The item shows signs of wear, odor, stains, washing, or damage</li>
          <li>The cloth label or tags are removed or damaged</li>
          <li>
            The item was marked as "Final Sale" or purchased during a clearance event (unless
            defective)
          </li>
        </ul>

        <h3 className='text-lg font-semibold text-gray-900 mb-3 mt-6'>Return Process</h3>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>Contact support@stedshop.com.ng with your order number and reason for return</li>
          <li>Upon approval, you'll receive return instructions</li>
          <li>
            Return shipping costs are borne by the customer, unless the return is due to our error
            (e.g., wrong or defective item)
          </li>
        </ul>

        <h3 className='text-lg font-semibold text-gray-900 mb-3 mt-6'>Refund Processing</h3>
        <ul className='list-disc pl-6 space-y-2 text-gray-700 mb-4'>
          <li>
            Once we receive and inspect the item and confirm it meets return conditions, we will
            process your refund
          </li>
          <li>Refunds are issued to the original payment method within 7–14 working days</li>
          <li>Original shipping fees are non-refundable, unless the return is due to our error</li>
        </ul>

        <h3 className='text-lg font-semibold text-gray-900 mb-3 mt-6'>Defective or Wrong Items</h3>
        <p className='text-gray-700'>
          If you receive a damaged, defective, or incorrect item, notify us within 48 hours of
          delivery. We will arrange a free return and full refund or replacement.
        </p>
      </Card>

      {/* 8. Product Description */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='products'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>8. Product Description & Pricing</h2>
        <p className='text-gray-700 mb-4'>
          We strive for accuracy in product descriptions, images, and pricing. However, Stedshop LTD
          does not warrant that content is error-free. In the event of a pricing error (e.g.,
          typographical mistake), we reserve the right to cancel the order and refund any amount
          paid within 10–15 business days.
        </p>
        <p className='text-gray-700'>
          Colors and appearance of products may vary slightly due to lighting or screen settings. By
          purchasing, you acknowledge this possibility.
        </p>
      </Card>

      {/* 9. Delivery */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='delivery'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>9. Delivery & Risk of Loss</h2>
        <ul className='list-disc pl-6 space-y-2 text-gray-700'>
          <li>All items are shipped under a shipment contract</li>
          <li>Risk of loss remains with Stedshop LTD until the item is delivered to you</li>
          <li>
            Once delivered and accepted (or left at your premises per delivery instructions), risk
            transfers to you
          </li>
          <li>
            Delivery partners will not enter private residences. Delivery is deemed complete once
            the item reaches your gate or designated drop-off point
          </li>
        </ul>
      </Card>

      {/* 10. Intellectual Property */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='ip'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>10. Intellectual Property</h2>
        <p className='text-gray-700'>
          All content on Stedshop.com.ng — including text, graphics, logos, images, and software —
          is owned or licensed by Stedshop LTD and protected by Nigerian and international
          intellectual property laws. You may not copy, reproduce, or use any content for commercial
          purposes without our prior written consent.
        </p>
      </Card>

      {/* 11. Third-Party Links */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='third-party'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>11. Third-Party Links & Vendors</h2>
        <p className='text-gray-700'>
          Stedshop.com.ng may contain links to third-party websites or list products from external
          vendors. We do not endorse, verify, or assume responsibility for such sites, products, or
          services. Any dealings with third parties are at your own risk.
        </p>
      </Card>

      {/* 12. Limitation of Liability */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='limitation'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>12. Limitation of Liability</h2>
        <p className='text-gray-700 mb-4'>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, STEDSHOP LTD SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, CONSEQUENTIAL, OR SPECIAL DAMAGES ARISING FROM YOUR USE OF THE WEBSITE OR
          PURCHASE OF PRODUCTS, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p className='text-gray-700'>
          OUR TOTAL LIABILITY TO YOU IN ANY MATTER ARISING FROM THESE TERMS SHALL NOT EXCEED THE
          AMOUNT PAID BY YOU FOR THE PRODUCT IN QUESTION.
        </p>
      </Card>

      {/* 13. Indemnity */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='indemnity'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>13. Indemnity</h2>
        <p className='text-gray-700 mb-3'>
          You agree to indemnify and hold harmless Stedshop LTD, its affiliates, officers, and
          employees from any claims, losses, or damages (including legal fees) arising from your:
        </p>
        <ul className='list-disc pl-6 space-y-2 text-gray-700'>
          <li>Breach of these Terms</li>
          <li>Violation of any law or third-party rights</li>
          <li>Misuse of the Website or products</li>
        </ul>
      </Card>

      {/* 14. Applicable Law */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='law'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>14. Applicable Law & Jurisdiction</h2>
        <p className='text-gray-700'>
          These Terms shall be governed by and construed in accordance with the laws of the Federal
          Republic of Nigeria. Any disputes shall be subject to the exclusive jurisdiction of the
          courts in Lagos, Nigeria.
        </p>
      </Card>

      {/* 15. Sale in Nigeria Only */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='territory'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>15. Sale in Nigeria Only</h2>
        <p className='text-gray-700'>
          Unless otherwise stated, Stedshop.com.ng operates exclusively for customers within
          Nigeria. We make no representation that products or content are appropriate for use
          outside Nigeria. If you access the site from outside Nigeria, you do so at your own risk
          and are responsible for compliance with local laws.
        </p>
      </Card>

      {/* 16. Termination */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='termination'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>16. Termination</h2>
        <p className='text-gray-700'>
          Stedshop LTD may, at its sole discretion, suspend or terminate your access to the Site
          without notice for any violation of these Terms or if deemed necessary to protect our
          rights, users, or business.
        </p>
      </Card>

      {/* 17. General */}
      <Card className='p-6 border-0 shadow-sm mb-8' id='general'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>17. General</h2>
        <ul className='list-disc pl-6 space-y-2 text-gray-700'>
          <li>
            These Terms, along with our Privacy Policy, constitute the entire agreement between you
            and Stedshop LTD
          </li>
          <li>Our failure to enforce any right is not a waiver of that right</li>
          <li>If any provision is held unenforceable, the remainder of the Terms remains valid</li>
        </ul>
      </Card>

      {/* Contact */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-gray-50'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>Contact Us</h2>
        <p className='text-gray-700 mb-4'>
          If you have any questions about these Terms & Conditions, please contact us at:
        </p>
        <p className='text-gray-700 font-semibold'>Email: support@stedshop.com.ng</p>
        <p className='text-gray-700 mt-2'>
          Address: No 3 Chinyelugo Udozor street awada obosi, Anambra, Nigeria
        </p>
      </Card>

      {/* Acknowledgment */}
      <Card className='p-6 border-0 shadow-sm mb-8 bg-blue-50'>
        <h2 className='text-xl font-bold text-gray-900 mb-4'>Acknowledgment</h2>
        <p className='text-gray-700 mb-4'>
          By using our Services, you acknowledge that you have read and understood these Terms &
          Conditions and agree to be bound by them.
        </p>
        <p className='text-gray-700 font-semibold'>Thank you for shopping with Stedshop LTD.</p>
      </Card>
    </div>
  );
};
